import { useEffect, useMemo, useState } from 'react';
import { App } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AssetService } from '~/app/services/asset/asset.service';
import { KmsDocumentService } from '~/app/services/kms/kms-document.service';
import type { GetKmsDocumentStatusResponse } from '~/app/services/kms/kms-document.types';
import {
  canRerunKmsAssetLink,
  isAwaitingKmsLink,
  isKmsAssetLinkSkipped,
  resolveKmsConfirmedDocumentType,
} from '~/app/services/kms/kms-document.utils';

export type KmsAssetLinkScopeKind = 'global' | 'asset';

export function useKmsAssetLinkEditor(params: {
  document: GetKmsDocumentStatusResponse | null;
  organizationId: string | null;
  isSuperAdmin: boolean;
}) {
  const { document, organizationId, isSuperAdmin } = params;
  const { t } = useTranslation('kms');
  const { notification } = App.useApp();
  const queryClient = useQueryClient();

  const [scopeKind, setScopeKind] = useState<KmsAssetLinkScopeKind>('global');
  const [assetId, setAssetId] = useState<string | undefined>();

  const assetsQuery = useQuery({
    queryKey: ['kms', 'asset-link-editor', organizationId],
    queryFn: () =>
      AssetService.listByOrganization({
        organizationId: organizationId!,
        page: 1,
        limit: 200,
      }),
    enabled: Boolean(organizationId),
  });

  const assetOptions = useMemo(
    () =>
      (assetsQuery.data?.data ?? []).map((asset) => ({
        value: asset.id,
        label: asset.assetName || asset.id,
      })),
    [assetsQuery.data],
  );

  useEffect(() => {
    if (!document) {
      return;
    }
    if (document.assetId) {
      setScopeKind('asset');
      setAssetId(document.assetId);
      return;
    }
    if (document.manualAssetId) {
      setScopeKind('asset');
      setAssetId(document.manualAssetId);
      return;
    }
    setScopeKind('global');
    setAssetId(undefined);
  }, [document?.documentId, document?.assetId, document?.manualAssetId]);

  const awaitingLink = isAwaitingKmsLink(document);
  const linkSkipped = isKmsAssetLinkSkipped(document);
  const confirmedType = resolveKmsConfirmedDocumentType(document);
  const canRerunLink = canRerunKmsAssetLink(document);
  const canEdit = Boolean(
    document?.organizationId &&
    document.linkScope != null &&
    (document.status === 'linked' || document.status === 'pending_review'),
  );

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!document) {
        throw new Error('missing document');
      }
      if (scopeKind === 'asset' && !assetId) {
        throw new Error(t('detail.assetLink.assetRequired'));
      }
      return KmsDocumentService.updateAssetLink({
        documentId: document.documentId,
        assetId: scopeKind === 'global' ? null : assetId!,
        isGlobal: scopeKind === 'global',
        organizationId,
        isSuperAdmin,
      });
    },
    onSuccess: async () => {
      notification.success({ message: t('detail.assetLink.saveSuccess') });
      await queryClient.invalidateQueries({
        queryKey: ['kms', 'document', document?.documentId],
      });
    },
    onError: (error) => {
      notification.error({
        message: t('detail.assetLink.saveError'),
        description:
          error instanceof Error
            ? error.message
            : t('detail.assetLink.saveError'),
      });
    },
  });

  const rerunLinkMutation = useMutation({
    mutationFn: async () => {
      if (!document) {
        throw new Error('missing document');
      }
      return KmsDocumentService.rerunAssetLink({
        documentId: document.documentId,
        organizationId,
        isSuperAdmin,
      });
    },
    onSuccess: async () => {
      notification.success({ message: t('detail.assetLink.rerunSuccess') });
      await queryClient.invalidateQueries({
        queryKey: ['kms', 'document', document?.documentId],
      });
      await queryClient.refetchQueries({
        queryKey: ['kms', 'document', document?.documentId],
      });
    },
    onError: (error) => {
      notification.error({
        message: t('detail.assetLink.rerunError'),
        description:
          error instanceof Error
            ? error.message
            : t('detail.assetLink.rerunError'),
      });
    },
  });

  return {
    awaitingLink,
    linkSkipped,
    confirmedType,
    canRerunLink,
    canEdit,
    scopeKind,
    setScopeKind,
    assetId,
    setAssetId,
    assetOptions,
    assetsLoading: assetsQuery.isLoading,
    linkScope: document?.linkScope ?? null,
    linkConfidence: document?.linkConfidence ?? null,
    manualAssetId: document?.manualAssetId ?? null,
    linkedAssetId: document?.assetId ?? null,
    linkedAssetName: document?.assetName ?? null,
    onSave: () => saveMutation.mutateAsync(),
    isSaving: saveMutation.isPending,
    onRerunLink: () => rerunLinkMutation.mutateAsync(),
    isRerunningLink: rerunLinkMutation.isPending,
  };
}
